/**
 * PQ Fulfilment Selector v1.4.1
 *   v1.4.1 (R21b — gate pin): live poll showed the theme's section refresh
 *         replaces the checkout button with a server-rendered ENABLED node
 *         mid-chain (~0.2–0.8s clickable windows). gateCheckout only styled
 *         the node it saw at call time. Fix: body.pqf-gate-locked CSS pin
 *         (fresh buttons are born unclickable; body survives re-renders) +
 *         an undebounced observer that re-disables replacements instantly
 *         (covers keyboard/Enter, which CSS pointer-events does not).
 *   v1.4 (R21 — selection latency): the _pqf stamp no longer re-fetches the
 *         cart between line writes. /cart/change.js already returns the full
 *         post-write cart, so the chain reuses each response as the next
 *         read AND as the final verification (N+1 calls instead of ~2N+1;
 *         measured 2.4s vs 6.6s on a 3–4 line cart, same conditions).
 *         Concurrent line writes were probed and are NOT safe — Shopify
 *         applies /cart/change.js as read-modify-write on the whole cart, so
 *         parallel requests silently drop sibling updates (verified live,
 *         probe E/F 26 Aug 2026). The chain stays strictly serial.
 *         The interim un-stamp when a store service is picked but no store is
 *         chosen yet no longer gates checkout (checkout is already blocked by
 *         "choose a store" in that state) — service clicks now respond
 *         instantly; only store picks and Standard re-stamps show
 *         "Confirming your selection…". One bounded retry re-runs the chain
 *         if verification fails (e.g. a third-party rewrite mid-chain —
 *         Bespoke's own JS rewrites line properties in the background).
 *         Failsafe raised 6s -> 10s; cart-change events during a stamp mark
 *         it dirty instead of re-triggering (kills the re-gate flicker and
 *         the multi-refresh storm — stock refresh now fires once per change).
 *   v1.3: fresh/frozen guard (order 16108, Black Rock VIC — frozen cart bought
 *         via a $0 generic C&C rate from 900km away). Detection mirrors the
 *         "Shipping: Fresh & Frozen" smart collection (312835801147): product
 *         type Dog Food Fresh / Cat Food Fresh / Fish Food Frozen OR tag
 *         "Fresh and Frozen", resolved via /products/{handle}.js (cached per
 *         handle, fails open — Bespoke v4.9 zero-rate guard is the backstop).
 *         Frozen carts: banner explains, Standard Shipping disabled,
 *         auto-switch to Click & Collect, checkout gated until a store is
 *         chosen.
 *   v1.2: (a) 2hr Delivery order window now has a floor as well as a ceiling —
 *         opens 9:00am daily (order 16036 selected 2hr at 1:51am because only
 *         the cutoff existed); (b) boot-time restore resets stale out-of-window
 *         2hr selections to standard; (c) boot-time _pqf heal — third-party
 *         apps (e.g. cart promo upsells) can rewrite line items and wipe our
 *         _pqf property, so restore now re-stamps lines even when the saved
 *         service is 'standard' (order 16036 had attributes but no _pqf).
 *   v1.1: checkout gated while _pqf line-item sync is pending/in-flight
 *         (fixes stale "standard|" reaching Bespoke Shipping when checkout
 *         starts before the async stamp lands — see order 15973). 6s failsafe
 *         guarantees the button always re-enables.
 * Modern replacement for the Retail Express Click & Collect *cart* UI.
 *
 * DATA CONTRACT (unchanged — do not modify):
 *   attributes[cnc-fulfilment-method]  -> 'home' | 'store'   (#cnc-mode-input)
 *   attributes[cnc-outlet]             -> REX outlet external id (#cnc-outlet)
 *   attributes[cnc-store-name]         -> outlet name (#cnc-store-name)
 *   attributes[cnc-store-address1..3|suburb|state|postcode|phone|email]
 * Extra (new, informational only):
 *   attributes[pq-delivery-service]    -> 'standard' | '2hr-delivery' | 'click-and-collect'
 *
 * Stock source: same REX endpoint as legacy:
 *   https://{apiPrefix}/api/shopify_stores/{subdomain}/outlets?variant_ids=..&qty-{id}=..
 *
 * MOCK MODE (for draft-theme testing where connector may be live-theme-only):
 *   append ?pq_mock=1 to the cart URL, or ?pq_mock=edge for edge cases.
 */
(function () {
  'use strict';

  var CONTACT_FIELDS = ['address1', 'address2', 'address3', 'suburb', 'state', 'postcode', 'phone', 'email'];
  var UBER_RADIUS_KM = 15;

  /* ---- fresh/frozen detection (v1.3) ----
     Mirrors the "Shipping: Fresh & Frozen" smart collection (312835801147),
     which is disjunctive on product type OR tag. Keep in sync with Bespoke
     Shipping's detection (collection primary / tag-regex fallback). */
  var FF_TYPES = ['dog food fresh', 'cat food fresh', 'fish food frozen'];
  var FF_TAG_RE = /fresh\s+and\s+frozen/i;

  // ---- 2hr Delivery order window --------------------------------------------
  // Explicit per-day order window (Sydney time), same for all stores.
  // Opens 9:00am every day; last order Mon-Fri 4:30pm, Sat 3:30pm, Sun 3:00pm.
  // Outside the window the 2hr option greys out. Both a floor and a ceiling:
  // with only the cutoff, the option came back at midnight (order 16036, 1:51am).
  var OPEN_TIMES   = { 0: '09:00', 1: '09:00', 2: '09:00', 3: '09:00', 4: '09:00', 5: '09:00', 6: '09:00' };
  var CUTOFF_TIMES = { 0: '15:00', 1: '16:30', 2: '16:30', 3: '16:30', 4: '16:30', 5: '16:30', 6: '15:30' };
  var DAY_IDX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  function sydneyNow() {
    var parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney', hour12: false,
      weekday: 'short', hour: '2-digit', minute: '2-digit'
    }).formatToParts(new Date());
    var out = {};
    parts.forEach(function (p) { out[p.type] = p.value; });
    return { day: DAY_IDX[(out.weekday || '').slice(0, 3)], mins: (parseInt(out.hour, 10) % 24) * 60 + parseInt(out.minute, 10) };
  }
  function toMins(hm) {
    var t = hm.split(':');
    return parseInt(t[0], 10) * 60 + parseInt(t[1], 10);
  }
  function fmtTime(hm) {
    var t = hm.split(':');
    var h = parseInt(t[0], 10), m = t[1];
    return (h > 12 ? h - 12 : h) + ':' + m + (h >= 12 ? 'pm' : 'am');
  }
  /* 'open' | 'before-open' | 'after-cutoff'. Fail open: never block orders on
   * a clock bug — a wrong $9.95 rate beats a checkout that won't work. */
  function expressWindowState() {
    try {
      var now = sydneyNow();
      if (now.day === undefined || isNaN(now.mins)) return 'open';
      if (now.mins < toMins(OPEN_TIMES[now.day])) return 'before-open';
      if (now.mins >= toMins(CUTOFF_TIMES[now.day])) return 'after-cutoff';
      return 'open';
    } catch (e) { return 'open'; }
  }
  function expressUnavailable() { return expressWindowState() !== 'open'; }
  function todayOpenLabel() {
    try { return fmtTime(OPEN_TIMES[sydneyNow().day]); } catch (e) { return '9:00am'; }
  }
  function todayCutoffLabel() {
    try { return fmtTime(CUTOFF_TIMES[sydneyNow().day]); } catch (e) { return '4:30pm'; }
  }

  var MOCK_OUTLETS = [
    { name: 'PETQuarters Broadmeadow', external_id: '2', contact: { address1: '4/7A Griffiths Road', suburb: 'Broadmeadow', state: 'NSW', postcode: '2292', phone: '02 4952 0000', email: 'broadmeadow@petquarters.com.au', latitude: '-32.922', longitude: '151.729' } },
    { name: 'PETQuarters Glendale', external_id: '3', contact: { address1: '5/25 Stockland Drive', suburb: 'Glendale', state: 'NSW', postcode: '2285', phone: '02 4954 0000', email: 'glendale@petquarters.com.au', latitude: '-32.932', longitude: '151.641' } },
    { name: 'PETQuarters Belmont', external_id: '4', contact: { address1: '467 Pacific Highway', suburb: 'Belmont', state: 'NSW', postcode: '2280', phone: '02 4945 0000', email: 'belmont@petquarters.com.au', latitude: '-33.033', longitude: '151.660' } },
    { name: 'PETQuarters Morisset', external_id: '5', contact: { address1: '8A Advantage Avenue', suburb: 'Morisset', state: 'NSW', postcode: '2264', phone: '02 4973 0000', email: 'morisset@petquarters.com.au', latitude: '-33.108', longitude: '151.487' } },
    { name: 'PETQuarters Rutherford', external_id: '6', contact: { address1: '58 Shipley Drive', suburb: 'Rutherford', state: 'NSW', postcode: '2320', phone: '02 4932 0000', email: 'rutherford@petquarters.com.au', latitude: '-32.715', longitude: '151.531' } },
    { name: 'PETQuarters Thornton', external_id: '7', contact: { address1: '4/2 New England Highway', suburb: 'Thornton', state: 'NSW', postcode: '2322', phone: '02 4966 0000', email: 'thornton@petquarters.com.au', latitude: '-32.783', longitude: '151.638' } },
    { name: 'PETQuarters Tuggerah', external_id: '9', contact: { address1: '2/186 Pacific Highway', suburb: 'Tuggerah', state: 'NSW', postcode: '2259', phone: '02 4353 0000', email: 'tuggerah@petquarters.com.au', latitude: '-33.311', longitude: '151.416' } },
    { name: 'PETQuarters Eastern Creek', external_id: '10', contact: { address1: '3/2 Goldsboro Glade', suburb: 'Eastern Creek', state: 'NSW', postcode: '2766', phone: '02 9672 0000', email: 'easterncreek@petquarters.com.au', latitude: '-33.803', longitude: '150.851' } }
  ];

  /* Known store coordinates — used when REX outlet data has no lat/lng.
     Keyed by outlet external_id, with postcode as a secondary key. */
  var STORE_COORDS = {};
  MOCK_OUTLETS.forEach(function (m) {
    var coord = { lat: parseFloat(m.contact.latitude), lng: parseFloat(m.contact.longitude) };
    STORE_COORDS[String(m.external_id)] = coord;
    STORE_COORDS['pc:' + m.contact.postcode] = coord;
  });

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371;
    var dLa = (lat2 - lat1) * Math.PI / 180;
    var dLo = (lng2 - lng1) * Math.PI / 180;
    var h = Math.sin(dLa / 2) * Math.sin(dLa / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLo / 2) * Math.sin(dLo / 2);
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function PQFulfilment(settings) {
    this.settings = settings || {};
    this.root = document.getElementById('pq-fulfilment');
    if (!this.root) return;
    this.form = this.root.closest('form') || document.querySelector('form[id^="cartForm-"]');
    this.mockMode = /[?&]pq_mock(=|&|$)/.test(location.search) ? (/[?&]pq_mock=edge/.test(location.search) ? 'edge' : 'basic') : null;
    this.service = 'standard';           // 'standard' | '2hr-delivery' | 'click-and-collect'
    this.selectedOutletId = null;
    this.outlets = [];                   // [{data, available, missing:[...] , km?}]
    this.customerLoc = null;
    this.loaded = false;
    this.hiddenInputs = {};
    this._touched = false;      // customer has made (or previously made) a selection
    this._restored = false;     // boot-time restore from cart attributes done
    this._restoring = false;    // suppress persist while restore outlet is pending
    this._restoreOutlet = null; // outlet id pending until outlets load
    this._pqfPending = false;   // _pqf sync scheduled or in flight — checkout gated
    this._pqfFailsafe = null;   // failsafe timer: never leave checkout locked
    this.hasFrozen = false;     // v1.3: cart contains fresh/frozen item(s)
    this.frozenTitles = [];     // v1.3: titles of the frozen items (for the banner)
    this._ffCache = {};         // v1.3: product handle -> frozen? (session cache)
    this._stdDescHTML = '';     // v1.3: original Standard card description (restore)
    this.buildHiddenInputs();
    this.buildUI();
    this.applyAttributes();
    this._booted = true;
    this.gateCheckout();
    this.fetchOutlets();
    this.watchCartChanges();
    window.__pqfWidget = this; // debug/integration handle
  }

  /* ---------- hidden inputs: exact legacy contract ---------- */

  PQFulfilment.prototype.buildHiddenInputs = function () {
    var holder = el('div');
    holder.style.display = 'none';
    var defs = [
      { id: 'cnc-mode-input', name: 'attributes[cnc-fulfilment-method]', value: 'home' },
      { id: 'cnc-outlet', name: 'attributes[cnc-outlet]', value: '' },
      { id: 'cnc-store-name', name: 'attributes[cnc-store-name]', value: '' },
      { id: 'pq-delivery-service', name: 'attributes[pq-delivery-service]', value: 'standard' }
    ];
    CONTACT_FIELDS.forEach(function (f) {
      defs.push({ id: 'cnc-contact-' + f, name: 'attributes[cnc-store-' + f + ']', value: '' });
    });
    var self = this;
    defs.forEach(function (d) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.id = d.id;
      input.name = d.name;
      input.value = d.value;
      input.classList.add('cnc-contact');
      holder.appendChild(input);
      self.hiddenInputs[d.id] = input;
    });
    this.root.appendChild(holder);
  };

  PQFulfilment.prototype.applyAttributes = function () {
    var storeMode = this.service !== 'standard';
    this.hiddenInputs['cnc-mode-input'].value = storeMode ? 'store' : 'home';
    this.hiddenInputs['pq-delivery-service'].value = this.service;
    var outlet = this.getSelectedOutlet();
    if (storeMode && outlet) {
      this.hiddenInputs['cnc-outlet'].value = outlet.data.external_id;
      this.hiddenInputs['cnc-store-name'].value = outlet.data.name;
      for (var i = 0; i < CONTACT_FIELDS.length; i++) {
        var f = CONTACT_FIELDS[i];
        this.hiddenInputs['cnc-contact-' + f].value = (outlet.data.contact && outlet.data.contact[f]) || '';
      }
    } else {
      this.hiddenInputs['cnc-outlet'].value = '';
      this.hiddenInputs['cnc-store-name'].value = '';
      for (var j = 0; j < CONTACT_FIELDS.length; j++) {
        this.hiddenInputs['cnc-contact-' + CONTACT_FIELDS[j]].value = '';
      }
    }
    if (this._booted && !this._restoring) this.persistAttributes();
  };

  /* ---------- AJAX persist: survives Shop Pay / dynamic checkout / direct /checkout ----------
   * Hidden inputs only reach the order when checkout starts from the cart form post.
   * Mirror the same attributes onto the cart itself via /cart/update.js so they
   * survive every checkout entry point. Debounced; fails silently (hidden inputs
   * remain as the backward-compatible fallback). */

  PQFulfilment.prototype.persistAttributes = function () {
    var self = this;
    clearTimeout(this._persistTimer);
    this._persistTimer = setTimeout(function () {
      var storeMode = self.service !== 'standard';
      var outlet = storeMode ? self.getSelectedOutlet() : null;
      var attrs = {
        'pq-delivery-service': self.service,
        'cnc-fulfilment-method': storeMode ? 'store' : 'home',
        'cnc-outlet': outlet ? String(outlet.data.external_id) : '',
        'cnc-store-name': outlet ? outlet.data.name : ''
      };
      for (var i = 0; i < CONTACT_FIELDS.length; i++) {
        var f = CONTACT_FIELDS[i];
        attrs['cnc-store-' + f] = outlet ? ((outlet.data.contact && outlet.data.contact[f]) || '') : '';
      }
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () { /* silent — hidden inputs remain the fallback */ });
    }, 400);
    this.syncLineProps();
  };

  /* ---------- _pqf line-item property mirror ----------
   * Bespoke Shipping's server-side macro cannot see cart attributes (not sent in
   * carrier rate requests), but line-item properties ARE. Mirror the selection as
   * `_pqf` = "<service>|<outlet>" on every cart line (leading underscore hides it
   * from the customer). Contract:
   *   standard                    -> "standard|"
   *   store mode + outlet chosen  -> e.g. "2hr-delivery|Glendale"
   *   store mode, no outlet yet   -> remove _pqf (checkout falls back to nearest-store)
   * Merge-don't-replace properties (_bespokeid/_bskey etc must survive); property
   * changes re-key the line, so change one line then re-fetch before the next. */

  PQFulfilment.prototype.pqfTarget = function () {
    if (this.service === 'standard') return 'standard|';
    var outlet = this.getSelectedOutlet();
    return outlet ? this.service + '|' + String(outlet.data.external_id) : null;
  };

  PQFulfilment.prototype.syncLineProps = function () {
    var self = this;
    /* v1.4: a null target (store mode, no store chosen yet) only STRIPS _pqf;
     * checkout is already blocked by the "choose a store" gate in that state,
     * so the strip runs in the background without locking the button. Real
     * stamps (standard re-stamp, store pick) gate until verified. */
    this._pqfRetryDone = false;
    this._setPqfPending(this.pqfTarget() !== null);
    clearTimeout(this._pqfTimer);
    this._pqfTimer = setTimeout(function () { self._runPqfSync(); }, 150);
  };

  /* Checkout stays gated while a _pqf stamp is scheduled or in flight, so a fast
   * click can no longer reach checkout against a stale tag (order 15973 bug).
   * Failsafe: whatever happens (network error, huge cart), the gate lifts after
   * 10s — a stuck checkout button is worse than a stale shipping quote.
   * (v1.4: was 6s, which the old ~1.65s/line loop could exceed on a 4-line
   * cart, force-opening checkout mid-stamp — the flicker. The chain runs
   * ~0.7s/line, so 10s covers ~13 lines with margin.) */
  PQFulfilment.prototype._setPqfPending = function (on) {
    var self = this;
    clearTimeout(this._pqfFailsafe);
    this._pqfPending = !!on;
    if (on) {
      this._pqfFailsafe = setTimeout(function () {
        self._pqfPending = false;
        self.gateCheckout();
      }, 10000);
    }
    this.gateCheckout();
  };

  PQFulfilment.prototype._runPqfSync = function () {
    var self = this;
    if (this._pqfBusy) { this._pqfDirty = true; return; }
    this._pqfBusy = true;
    this._pqfDirty = false;
    var target = this.pqfTarget();
    var guard = 0;

    function stamped(item) {
      return ((item.properties && item.properties._pqf) || null) === target;
    }

    function finish(verifiedOk) {
      /* One bounded retry per user intent: a failed verify usually means a
       * third-party script rewrote lines mid-chain (Bespoke does this) or a
       * write blipped — one fresh pass heals it. Never loops: the flag only
       * resets on the next user action / cart event. */
      if (verifiedOk === false && !self._pqfRetryDone) {
        self._pqfRetryDone = true;
        self._pqfDirty = true;
      }
      self._pqfBusy = false;
      if (self._pqfDirty) { self._runPqfSyncRequeue(); return; }
      self._setPqfPending(false);
    }

    /* v1.4 chain: ONE /cart.js read, then serial /cart/change.js writes that
     * each reuse the returned post-write cart as the next read — no re-fetch
     * between lines. Serial is mandatory: concurrent /cart/change.js requests
     * are read-modify-write on the whole cart and drop sibling updates
     * (probed live 26 Aug 2026). Final response doubles as verification. */
    function step(cart) {
      if (++guard > 25) { finish(false); return; }
      var items = cart.items || [];
      var line = null;
      for (var i = 0; i < items.length; i++) {
        if (!stamped(items[i])) { line = items[i]; break; }
      }
      if (!line) { finish(true); return; } // every line verified against target
      var props = {};
      var src = line.properties || {};
      for (var k in src) { if (k !== '_pqf') props[k] = src[k]; }
      if (target !== null) props._pqf = target;
      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: props })
      }).then(function (r) {
        if (!r.ok) throw new Error('change failed ' + r.status);
        return r.json();
      }).then(step) // response body IS the updated cart
        .catch(function () { finish(false); });
    }

    this.getCart().then(step).catch(function () { finish(false); });
  };

  /* Re-arm after a dirty run without resetting the retry budget. */
  PQFulfilment.prototype._runPqfSyncRequeue = function () {
    var self = this;
    this._setPqfPending(this.pqfTarget() !== null);
    clearTimeout(this._pqfTimer);
    this._pqfTimer = setTimeout(function () { self._runPqfSync(); }, 150);
  };

  /* ---------- UI ---------- */

  PQFulfilment.prototype.buildUI = function () {
    var self = this;
    this.root.classList.add('pqf');

    if (this.mockMode) {
      this.root.appendChild(el('div', 'pqf-mockbanner', '&#9888; TEST MODE &mdash; simulated store data (pq_mock)'));
    }

    this.root.appendChild(el('div', 'pqf-step', 'How would you like to get your order?'));

    var opts = [
      { key: 'standard', icon: '&#128230;', title: 'Standard Shipping', badge: null, desc: 'Delivered Australia-wide in 2\u20137 business days.' },
      { key: '2hr-delivery', icon: '&#9889;', title: '2hr Delivery', badge: '<span class="pqf-badge pqf-badge--fast">FASTEST</span>', desc: 'Within 2 hours, from a store within ' + UBER_RADIUS_KM + 'km. Flat $9.95 fee.' },
      { key: 'click-and-collect', icon: '&#127978;', title: 'Click &amp; Collect', badge: '<span class="pqf-badge pqf-badge--free">FREE</span>', desc: 'Ready to collect in-store, usually within 90 minutes. We\u2019ll email you when it\u2019s ready.' }
    ];

    this.optEls = {};
    opts.forEach(function (o) {
      var card = el('div', 'pqf-opt' + (o.key === 'standard' ? ' is-selected' : ''));
      card.setAttribute('role', 'radio');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-checked', o.key === 'standard' ? 'true' : 'false');
      card.dataset.service = o.key;
      card.innerHTML =
        '<div class="pqf-opt-icon pqf-opt-icon--' + o.key + '">' + o.icon + '</div>' +
        '<div class="pqf-opt-main"><div class="pqf-opt-title">' + o.title + (o.badge || '') + '</div>' +
        '<div class="pqf-opt-desc">' + o.desc + '</div></div>' +
        '<div class="pqf-opt-radio"></div>';
      var winState = o.key === '2hr-delivery' ? expressWindowState() : 'open';
      if (winState !== 'open') {
        card.classList.add('is-disabled');
        card.setAttribute('aria-disabled', 'true');
        card.querySelector('.pqf-opt-desc').innerHTML = winState === 'before-open'
          ? 'Available from ' + todayOpenLabel() + ' \u2014 order before ' +
            todayCutoffLabel() + ' for delivery today.'
          : 'Closed for today \u2014 order before ' +
            todayCutoffLabel() + ' for 2hr Delivery.';
        var badge = card.querySelector('.pqf-badge'); if (badge) badge.remove();
      } else {
        card.addEventListener('click', function () { self.setService(o.key); });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.setService(o.key); }
        });
      }
      self.root.appendChild(card);
      self.optEls[o.key] = card;
    });

    // v1.3: remember Standard's original description so the frozen guard can restore it
    var stdDescEl = this.optEls['standard'].querySelector('.pqf-opt-desc');
    this._stdDescHTML = stdDescEl ? stdDescEl.innerHTML : '';

    // v1.3: fresh/frozen banner — sits between the step header and the options.
    // Inserted before the first option card; hidden until a frozen item is detected.
    this.frozenNote = el('div', 'pqf-frozen-note',
      '&#10052;&#65039; <b>Fresh &amp; frozen items in your cart</b> \u2014 these need to stay cold, so they can\u2019t be posted. Your order is available by <b>Click &amp; Collect</b> or <b>2hr Delivery</b> from one of our stores.');
    this.root.insertBefore(this.frozenNote, this.optEls['standard']);

    // store finder (hidden until a store-based option is chosen)
    this.finder = el('div', 'pqf-finder');
    this.finderLabel = el('div', 'pqf-step', 'Choose your store');
    this.finder.appendChild(this.finderLabel);

    var pcRow = el('div', 'pqf-pcrow');
    this.pcInput = document.createElement('input');
    this.pcInput.type = 'text';
    this.pcInput.inputMode = 'numeric';
    this.pcInput.maxLength = 4;
    this.pcInput.placeholder = 'Postcode \u2014 sort stores by distance';
    this.pcInput.className = 'pqf-pcinput';
    this.pcInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); self.sortByPostcode(); }
    });
    var pcBtn = el('button', 'pqf-pcbtn', 'Find');
    pcBtn.type = 'button';
    pcBtn.addEventListener('click', function () { self.sortByPostcode(); });
    pcRow.appendChild(this.pcInput);
    pcRow.appendChild(pcBtn);
    this.finder.appendChild(pcRow);

    this.pcMsg = el('div', 'pqf-pcmsg');
    this.finder.appendChild(this.pcMsg);

    this.storeList = el('div', 'pqf-stores');
    this.finder.appendChild(this.storeList);

    this.uberNote = el('div', 'pqf-ubernote',
      '&#9889; Your address must be within <b>' + UBER_RADIUS_KM + 'km</b> of this store \u2014 select <b>\u201C2hr Delivery ($9.95)\u201D</b> at checkout.');
    this.finder.appendChild(this.uberNote);

    this.root.appendChild(this.finder);

    this.hint = el('div', 'pqf-hint');
    this.root.appendChild(this.hint);
  };

  PQFulfilment.prototype.setService = function (key) {
    if (key === '2hr-delivery' && expressUnavailable()) return;
    if (key === 'standard' && this.hasFrozen) return; // v1.3: frozen carts can't ship standard
    this._touched = true;
    if (this.service === key) return;
    this.service = key;
    this.selectedOutletId = null;
    for (var k in this.optEls) {
      var sel = k === key;
      this.optEls[k].classList.toggle('is-selected', sel);
      this.optEls[k].setAttribute('aria-checked', sel ? 'true' : 'false');
    }
    var storeMode = key !== 'standard';
    this.finder.classList.toggle('is-open', storeMode);
    this.finderLabel.textContent = key === '2hr-delivery'
      ? 'Choose the store that will deliver to you'
      : 'Choose your store';
    if (storeMode) this.renderStores();
    this.applyAttributes();
    this.gateCheckout();
  };

  PQFulfilment.prototype.getSelectedOutlet = function () {
    for (var i = 0; i < this.outlets.length; i++) {
      if (this.outlets[i].data.external_id === this.selectedOutletId) return this.outlets[i];
    }
    return null;
  };

  /* ---------- stock fetch (same REX endpoint as legacy) ---------- */

  PQFulfilment.prototype.getCart = function () {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  };

  PQFulfilment.prototype.fetchOutlets = function () {
    var self = this;
    this.getCart().then(function (cart) {
      self.cartItems = cart.items || [];
      self.restoreFromCart(cart);
      self.updateFrozen(cart); // v1.3: async — applies frozen UI when resolved
      if (!self.cartItems.length) { self.outlets = []; self.loaded = true; return; }

      if (self.mockMode) {
        var stock = {};
        var outlets = MOCK_OUTLETS.map(function (o, idx) {
          var s = {};
          self.cartItems.forEach(function (it, itemIdx) {
            // edge mode: Morisset missing first item; basic: everything in stock
            s[it.variant_id] = (self.mockMode === 'edge' && o.external_id === '5' && itemIdx === 0) ? 0 : 99;
          });
          return { name: o.name, external_id: o.external_id, contact: o.contact, stock: s };
        });
        self.ingestOutlets(outlets);
        return;
      }

      var url = 'https://' + self.settings.apiPrefix + '/api/shopify_stores/' + self.settings.subdomain +
        '/outlets?variant_ids=' + self.cartItems.map(function (i) { return i.variant_id; }).join(',');
      self.cartItems.forEach(function (i) { url += '&qty-' + i.variant_id + '=' + i.quantity; });

      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (json) { self.ingestOutlets(json.outlets || []); })
        .catch(function (err) {
          console.warn('[PQF] outlet fetch failed', err);
          self.loaded = true;
          self.fetchFailed = true;
          self._restoreOutlet = null;
          self._restoring = false; // keep prior server-side state untouched on failure
          if (self.service !== 'standard') self.renderStores();
          self.gateCheckout();
        });
    });
  };

  /* ---------- boot-time restore ----------
   * Selection now persists server-side (cart attributes + _pqf), so the widget must
   * re-adopt it after a page load — otherwise the UI shows Standard while the cart
   * still says C&C/2hr. Only runs once; only marks _touched when a prior
   * pq-delivery-service attribute exists (untouched visitors are never stamped). */

  PQFulfilment.prototype.restoreFromCart = function (cart) {
    if (this._restored) return;
    this._restored = true;
    var attrs = cart.attributes || {};
    var svc = attrs['pq-delivery-service'];
    if (!svc) return; // never selected anything — leave untouched
    this._touched = true;
    if (svc !== '2hr-delivery' && svc !== 'click-and-collect') {
      // standard: default UI already correct, but heal the _pqf stamp — third-party
      // apps that rewrite cart lines wipe our properties (order 16036 pattern)
      this.syncLineProps();
      return;
    }
    if (svc === '2hr-delivery' && expressUnavailable()) {
      // prior selection no longer valid right now (before 9am or past cutoff) —
      // fall back to standard and re-persist so stale 2hr intent can't reach checkout
      this.applyAttributes();
      this.gateCheckout();
      return;
    }
    this._restoreOutlet = (attrs['cnc-outlet'] != null && attrs['cnc-outlet'] !== '') ? String(attrs['cnc-outlet']) : null;
    this._restoring = this._restoreOutlet != null; // suppress persist until outlet re-applied
    this.setService(svc); // renders store list (loading state until outlets arrive)
  };

  PQFulfilment.prototype.ingestOutlets = function (rawOutlets) {
    var self = this;
    this.outlets = rawOutlets.map(function (o) {
      var missing = [];
      self.cartItems.forEach(function (it) {
        var have = (o.stock && o.stock[it.variant_id]) || 0;
        if (have < it.quantity) missing.push(it.product_title || it.title || 'Item');
      });
      return { data: o, available: missing.length === 0, missing: missing, km: undefined };
    });
    this.loaded = true;
    this.fetchFailed = false;
    if (this._restoreOutlet != null) {
      var pending = this._restoreOutlet;
      this._restoreOutlet = null;
      this._restoring = false;
      for (var ri = 0; ri < this.outlets.length; ri++) {
        var ro = this.outlets[ri];
        if (String(ro.data.external_id) === pending && ro.available) { this.selectedOutletId = ro.data.external_id; break; }
      }
      this.applyAttributes(); // re-persist restored selection (or cleared, if store now unavailable)
    }
    if (this.customerLoc) this.applyDistances();
    if (this.service !== 'standard') this.renderStores();
    this.gateCheckout();
  };

  /* ---------- fresh/frozen guard (v1.3) ----------
   * Cart AJAX (/cart.js) doesn't expose tags or type, so resolve each unique
   * product handle via /products/{handle}.js (CDN-cached, one fetch per handle
   * per session). Fails open per product: the widget is UX — Bespoke v4.9's
   * zero-rate guard at checkout is the enforcement backstop. */

  PQFulfilment.prototype.updateFrozen = function (cart) {
    var self = this;
    var items = (cart && cart.items) || [];
    if (!items.length) { this._setFrozen(false, []); return; }
    var handles = [], seen = {};
    items.forEach(function (it) {
      var h = it.handle;
      if (h && !seen[h]) { seen[h] = true; handles.push({ handle: h, title: it.product_title || it.title || 'Item' }); }
    });
    Promise.all(handles.map(function (entry) {
      if (self._ffCache[entry.handle] !== undefined) {
        return Promise.resolve({ entry: entry, frozen: self._ffCache[entry.handle] });
      }
      return fetch('/products/' + entry.handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('product fetch ' + r.status); return r.json(); })
        .then(function (p) {
          var tags = (p.tags || []).join(',');
          var type = String(p.type || '').toLowerCase().trim();
          var frozen = FF_TAG_RE.test(tags) || FF_TYPES.indexOf(type) !== -1;
          self._ffCache[entry.handle] = frozen;
          return { entry: entry, frozen: frozen };
        })
        .catch(function () { return { entry: entry, frozen: false }; }); // fail open
    })).then(function (results) {
      var titles = [];
      results.forEach(function (r) { if (r.frozen) titles.push(r.entry.title); });
      self._setFrozen(titles.length > 0, titles);
    });
  };

  PQFulfilment.prototype._setFrozen = function (has, titles) {
    var changed = this.hasFrozen !== has;
    this.hasFrozen = has;
    this.frozenTitles = titles;
    this.applyFrozenUI(changed);
  };

  PQFulfilment.prototype.applyFrozenUI = function (changed) {
    var std = this.optEls && this.optEls['standard'];
    if (!std) return;
    if (this.frozenNote) this.frozenNote.classList.toggle('is-visible', this.hasFrozen);
    var desc = std.querySelector('.pqf-opt-desc');
    if (this.hasFrozen) {
      std.classList.add('is-disabled');
      std.setAttribute('aria-disabled', 'true');
      if (desc) desc.innerHTML = 'Not available \u2014 fresh/frozen items can\u2019t be posted.';
      if (this.service === 'standard') {
        // Auto-switch: frozen orders must come from a store. Opens the store
        // finder; checkout stays gated until a store is chosen (gateCheckout).
        this.setService('click-and-collect');
        return; // setService re-renders, re-persists and re-gates
      }
    } else {
      std.classList.remove('is-disabled');
      std.removeAttribute('aria-disabled');
      if (desc) desc.innerHTML = this._stdDescHTML;
      // No auto-switch back: an explicit C&C/2hr choice stays the customer's.
    }
    if (changed && this.service !== 'standard') this.renderStores();
    this.gateCheckout();
  };

  /* ---------- postcode sort ---------- */

  PQFulfilment.prototype.sortByPostcode = function () {
    var self = this;
    var pc = (this.pcInput.value || '').trim();
    this.pcMsg.textContent = '';
    if (!/^\d{4}$/.test(pc)) {
      this.pcMsg.textContent = 'Please enter a 4-digit Australian postcode.';
      return;
    }
    this.pcMsg.textContent = 'Locating\u2026';

    var geocodeZippo = function () {
      return fetch('https://api.zippopotam.us/AU/' + pc)
        .then(function (r) { if (!r.ok) throw new Error('not found'); return r.json(); })
        .then(function (json) {
          var place = json.places && json.places[0];
          if (!place) throw new Error('not found');
          return { lat: parseFloat(place.latitude), lng: parseFloat(place.longitude) };
        });
    };
    var geocodeNominatim = function () {
      return fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=au&postalcode=' + pc)
        .then(function (r) { if (!r.ok) throw new Error('not found'); return r.json(); })
        .then(function (json) {
          if (!json.length) throw new Error('not found');
          return { lat: parseFloat(json[0].lat), lng: parseFloat(json[0].lon) };
        });
    };

    geocodeZippo()
      .catch(geocodeNominatim)
      .then(function (loc) {
        if (!isFinite(loc.lat) || !isFinite(loc.lng)) throw new Error('bad coords');
        self.customerLoc = loc;
        self.pcMsg.textContent = '';
        self.applyDistances();
        self.renderStores();
      })
      .catch(function () {
        self.pcMsg.textContent = 'We couldn\u2019t find that postcode \u2014 stores shown unsorted.';
      });
  };

  PQFulfilment.prototype.applyDistances = function () {
    var loc = this.customerLoc;
    if (!loc) return;
    this.outlets.forEach(function (o) {
      var c = o.data.contact || {};
      var lat = parseFloat(c.latitude), lng = parseFloat(c.longitude);
      if (!isFinite(lat) || !isFinite(lng)) {
        var fallback = STORE_COORDS[String(o.data.external_id)];
        if (fallback) { lat = fallback.lat; lng = fallback.lng; }
      }
      o.km = (isFinite(lat) && isFinite(lng)) ? haversineKm(loc.lat, loc.lng, lat, lng) : undefined;
    });
    this.outlets.sort(function (a, b) {
      if (a.km == null && b.km == null) return 0;
      if (a.km == null) return 1;
      if (b.km == null) return -1;
      return a.km - b.km;
    });
  };

  /* ---------- store list rendering ---------- */

  PQFulfilment.prototype.renderStores = function () {
    var self = this;

    /* Signature guard: rebuilding the list is visually destructive on mobile
       (the height-animated finder collapses to the loading placeholder, looking
       like the selector opening/closing repeatedly). Skip the rebuild when
       nothing visible would change, and keep showing cached stores while a
       background stock refresh is in flight (stale-while-revalidate). */
    var sig;
    if (!this.loaded && !this.outlets.length) sig = 'loading';
    else if (this.fetchFailed) sig = 'failed';
    else if (!this.outlets.length) sig = 'empty';
    else sig = this.service + '|' + String(this.selectedOutletId) + '|' + this.outlets.map(function (o) {
      return o.data.external_id + ':' + (o.available ? 1 : 0) + ':' + o.missing.join(',') + ':' + (o.km != null ? o.km.toFixed(1) : '');
    }).join(';');
    if (sig === this._storesSig) return;
    this._storesSig = sig;

    this.storeList.innerHTML = '';

    if (sig === 'loading') {
      this.storeList.appendChild(el('div', 'pqf-loading', 'Checking stock at your local stores\u2026'));
      return;
    }
    if (this.fetchFailed) {
      this.storeList.appendChild(el('div', 'pqf-error', this.hasFrozen
        ? 'We couldn\u2019t check store stock right now. Please try again shortly.'
        : 'We couldn\u2019t check store stock right now. Please choose Standard Shipping, or try again shortly.'));
      return;
    }
    if (!this.outlets.length) {
      this.storeList.appendChild(el('div', 'pqf-error', 'No stores are available for the items in your cart.'));
      return;
    }

    this.outlets.forEach(function (o) {
      var c = o.data.contact || {};
      var card = el('div', 'pqf-store' + (o.available ? '' : ' is-disabled') +
        (o.data.external_id === self.selectedOutletId ? ' is-selected' : ''));
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', o.data.external_id === self.selectedOutletId ? 'true' : 'false');
      if (o.available) card.setAttribute('tabindex', '0');

      var addr = [c.address1, c.address2, c.address3].filter(Boolean).join(', ');
      var line2 = [c.suburb, c.state, c.postcode].filter(Boolean).join(' ');
      var stockLine = o.available
        ? '<div class="pqf-stock pqf-stock--ok">&#10003; All items in stock</div>'
        : '<div class="pqf-stock pqf-stock--bad">&#10007; ' +
          (o.missing.length === 1 ? o.missing[0] + ' unavailable here' : o.missing.length + ' items unavailable here') +
          '</div>';

      card.innerHTML =
        '<div class="pqf-store-row"><div class="pqf-store-name">' + o.data.name + '</div>' +
        (o.km != null ? '<div class="pqf-store-dist">' + o.km.toFixed(1) + ' km</div>' : '') + '</div>' +
        '<div class="pqf-store-addr">' + [addr, line2].filter(Boolean).join(', ') + '</div>' +
        stockLine +
        '<div class="pqf-store-more">' + (c.phone ? 'Phone: ' + c.phone + ' &middot; ' : '') +
        (self.service === '2hr-delivery' ? 'We\u2019ll email you when the driver is on their way.' : 'We\u2019ll email you when your order is ready.') + '</div>';

      if (o.available) {
        var pickStore = function () {
          self.selectedOutletId = o.data.external_id;
          self.renderStores();
          self.applyAttributes();
          self.gateCheckout();
        };
        card.addEventListener('click', pickStore);
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pickStore(); }
        });
      }
      self.storeList.appendChild(card);
    });

    this.uberNote.classList.toggle('is-visible', this.service === '2hr-delivery' && !!this.selectedOutletId);
  };

  /* ---------- checkout gating ---------- */

  PQFulfilment.prototype.gateCheckout = function () {
    var btn = document.querySelector('button[name="checkout"]') || document.querySelector('input[name="checkout"]');
    var storeMode = this.service !== 'standard';
    var outlet = this.getSelectedOutlet();
    var selectionOk = !storeMode || !!outlet;
    var pending = !!this._pqfPending; // _pqf stamp scheduled/in flight
    var frozenStd = this.hasFrozen && !storeMode; // v1.3: transient pre-auto-switch state
    var ok = selectionOk && !pending && !frozenStd;

    if (btn) {
      btn.disabled = !ok;
      btn.classList.toggle('pqf-gated', !ok);
    }
    /* v1.4.1: replacement-proof pin — see pinGate() */
    this._gateOk = ok;
    if (document.body) document.body.classList.toggle('pqf-gate-locked', !ok);
    this.pinGate();
    if (frozenStd) {
      this.hint.className = 'pqf-hint pqf-hint--err';
      this.hint.innerHTML = '&#10052;&#65039; Fresh/frozen items \u2014 choose Click &amp; Collect or 2hr Delivery from a store.';
    } else if (storeMode && !outlet) {
      this.hint.className = 'pqf-hint pqf-hint--err';
      this.hint.innerHTML = this.service === '2hr-delivery'
        ? 'Select the store that will deliver your order to continue.'
        : 'Select a store to continue.';
    } else if (pending) {
      this.hint.className = 'pqf-hint';
      this.hint.innerHTML = 'Confirming your selection&hellip;';
    } else if (!storeMode) {
      this.hint.className = 'pqf-hint';
      this.hint.innerHTML = 'Shipping calculated at checkout.';
    } else {
      this.hint.className = 'pqf-hint';
      this.hint.innerHTML = (this.service === '2hr-delivery' ? '&#9889; 2hr Delivery ($9.95) from ' : '&#127978; Collecting from ') +
        '<b>' + outlet.data.name + '</b>';
    }
  };

  /* v1.4.1: the theme's section refresh replaces the checkout button with a
   * server-rendered (enabled) node while the stamp chain runs; gateCheckout
   * only styles the node present at call time, and the busy-aware watcher
   * defers its re-run until the chain lands — leaving the fresh node
   * clickable for 0.2–0.8s (measured live, 27 Aug). Two layers close it:
   *   1. body.pqf-gate-locked (pq-fulfilment.css): freshly inserted buttons
   *      are born unclickable — body is never replaced by section renders.
   *   2. This undebounced observer: re-disables any enabled checkout button
   *      the moment it appears or has its disabled attribute stripped
   *      (covers keyboard/Enter, which CSS pointer-events cannot).
   * The observer is enforcement-only; gate STATE changes solely in
   * gateCheckout. Setting disabled=true re-fires the observer once; the
   * re-entry sees the button already disabled and no-ops (no loop). */
  PQFulfilment.prototype.pinGate = function () {
    if (this._pinObs || !window.MutationObserver || !document.body) return;
    var self = this;
    this._pinObs = new MutationObserver(function () {
      if (self._gateOk) return;
      var b = document.querySelector('button[name="checkout"]') ||
              document.querySelector('input[name="checkout"]');
      if (b && !b.disabled) { b.disabled = true; b.classList.add('pqf-gated'); }
    });
    this._pinObs.observe(document.body,
      { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  };

  /* ---------- cart change watcher (replaces DOMSubtreeModified hack) ---------- */

  PQFulfilment.prototype.watchCartChanges = function () {
    var self = this;
    var timer = null;
    function onCartChanged() {
      clearTimeout(timer);
      timer = setTimeout(function () {
        self.loaded = false;
        if (self.service !== 'standard') self.renderStores();
        self.fetchOutlets();
        /* v1.4: while our own chain is writing, don't spawn a competing sync
         * (that re-gated checkout in a loop — the flicker); mark dirty so the
         * running chain re-checks once it lands. Genuine new lines are caught
         * either way: the dirty re-run reads the cart fresh. */
        if (self._touched) {
          if (self._pqfBusy) { self._pqfDirty = true; }
          else { self.syncLineProps(); }
        }
      }, 800);
    }
    /* Event bus: fires on every template (drawer adds on collection/search
       pages included) — pq:cart-updated comes from pq-drawer-extras after
       any /cart mutation; cart:update is the theme's own bus. */
    document.addEventListener('pq:cart-updated', onCartChanged);
    document.addEventListener('cart:update', onCartChanged);
    /* Cart-page fallback: direct DOM observation (no drawer-extras needed). */
    var target = document.querySelector('[data-cart-items]');
    if (target && window.MutationObserver) {
      new MutationObserver(onCartChanged).observe(target, { childList: true, subtree: true });
    }
  };

  window.PQFulfilment = PQFulfilment;
})();
